import { Link } from "@mui/material";
import NextLink from "next/link";
import regexifyString from "regexify-string";

type Entity = { uuid?: string | null; name?: string | null; title?: string | null } | null;

/** Who a visitor on a general share link said they were; not verified. */
type GuestDetails = { first_name?: string | null; last_name?: string | null } | null;

export type HydratableNotification = {
  body?: string | null;
  contact?: { name?: string | null } | null;
  float?: Entity;
  submission?: Entity;
  job?: Entity;
  candidate?: Entity;
  attributes?: { duration?: string | null; guest?: GuestDetails; view_context?: unknown } | null;
};

/**
 * The name to show for the contact. A guest message is recorded against the
 * contact whose email the guest typed, which proves nothing, so it shows the
 * name the guest gave, flagged as unverified.
 */
function contactName(notification: HydratableNotification): string {
  const guest = notification.attributes?.guest;
  const guestName = [guest?.first_name, guest?.last_name].filter(Boolean).join(" ").trim();

  if (guestName) {
    return `${guestName} (unverified)`;
  }

  return notification.contact?.name || "Unknown";
}

export default function hydrateNotification(notification: HydratableNotification) {
  // Uses Next's Link (client-side nav) rather than a plain href, since a full
  // page load would reset the contextual-back-navigation history tracking.
  // Falls back to plain text when the entity has no uuid to link to — the
  // activity feeds pass interaction payloads that aren't always hydrated.
  const createEntityLink = (text: string | null | undefined, href: string | null, index: number) =>
    href ? (
      <Link key={index} component={NextLink} href={href}>
        {text}
      </Link>
    ) : (
      <span key={index}>{text}</span>
    );

  const entityHref = (entity: Entity | undefined, section: string) =>
    entity?.uuid ? `/${section}/${entity.uuid}` : null;

  // Define the patterns and their replacements
  const patterns: Record<string, string | ReturnType<typeof createEntityLink>> = {
    "{contact}": contactName(notification),
    "{float}": notification.float
      ? createEntityLink(notification.float.name, entityHref(notification.float, "floats"), 2)
      : "Unknown",
    "{submission}": notification.submission
      ? createEntityLink(
          notification.submission.name,
          entityHref(notification.submission, "submissions"),
          4
        )
      : "Unknown",
    "{job}": notification.job
      ? createEntityLink(
          notification.job.name || notification.job.title,
          entityHref(notification.job, "jobs"),
          5
        )
      : "Unknown",
    "{candidate}": notification.candidate
      ? createEntityLink(
          notification.candidate.name,
          entityHref(notification.candidate, "candidates"),
          3
        )
      : "Unknown",
    "{duration}": notification.attributes?.duration ? notification.attributes.duration : "",
  };

  // Use regexifyString to replace patterns with Links
  return regexifyString({
    pattern: new RegExp(`(${Object.keys(patterns).join("|")})`, "g"),
    decorator: (match: string) => patterns[match],
    input: notification.body ?? "",
  });
}
